import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  Autocomplete,
  IconButton,
  Tooltip,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  AppBar,
  Toolbar,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  Popover,
  Badge,
  FormControlLabel,
  Switch,
  LinearProgress,
} from '@mui/material';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import api from '../../services/api';
import { styled, type Theme, useTheme, alpha } from '@mui/material/styles';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useHelpContent } from '../../utils/HelpUtils';
import { hasDuplicateContent, getDuplicateContentMessage } from '../../utils/CategoryUtils';
import { Category } from '../../types/Category';
import { DropdownFieldTooltip } from '../DropdownFieldTooltip';
import {
  getApiOrigin,
  htmlToPlainWithBlockSpaces,
  prepareContentRichPreviewHtml,
  contentTextLooksRtl,
} from '../../utils/contentRichPreviewHtml';

/** When `file.type` is empty (some browsers/OS), infer kind from extension. */
function guessMediaKind(file: File): 'image' | 'video' | 'audio' | null {
  const t = (file.type || '').toLowerCase();
  if (t.startsWith('image/')) return 'image';
  if (t.startsWith('video/')) return 'video';
  if (t.startsWith('audio/')) return 'audio';
  const name = (file.name || '').toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) return 'image';
  if (/\.(mp4|m4v|mov|mkv|avi)$/i.test(name)) return 'video';
  if (/\.(webm|ogv)$/i.test(name)) return 'video';
  if (/\.(mp3|wav|m4a|aac|flac|opus)$/i.test(name)) return 'audio';
  if (/\.ogg$/i.test(name)) return 'audio';
  return null;
}

/** Remove inline video/audio from HTML so clips are not stored inside the rich text body. */
function stripVideoAudioFromHtml(html: string): string {
  if (!html) return html;
  return html
    .replace(/<video\b[^>]*>[\s\S]*?<\/video>/gi, '')
    .replace(/<audio\b[^>]*>[\s\S]*?<\/audio>/gi, '')
    .replace(/<video\b[^>]*\/\s*>/gi, '')
    .replace(/<audio\b[^>]*\/\s*>/gi, '');
}

function stripVideoAudioFromDelta(delta: { ops?: unknown[] }): { ops: unknown[] } {
  const ops = (delta?.ops || []).filter((op: any) => {
    if (op.insert && typeof op.insert === 'object') {
      if (op.insert.simpleVideo != null || op.insert.simpleAudio != null) return false;
    }
    return true;
  });
  return { ops };
}

function withMediaAccessTokenOnUrl(url: string): string {
  const token = localStorage.getItem('token');
  if (!token || !url) return url;
  const enc = encodeURIComponent(token);
  if (url.includes('access_token=')) return url;
  return `${url}${url.includes('?') ? '&' : '?'}access_token=${enc}`;
}

/** Resolve pasted or relative media URLs for the external players (with auth). */
function toAbsoluteMediaUrlForPlayback(src: string): string {
  let u = (src || '').trim();
  if (!u) return u;
  if (!u.startsWith('http')) {
    const origin = getApiOrigin();
    const path = u.startsWith('/') ? u : `/${u}`;
    u = origin ? `${origin}${path}` : path;
  }
  return withMediaAccessTokenOnUrl(u);
}

/**
 * Pasted HTML often includes `<video>` / `<audio>`; those must not go through `dangerouslyPasteHTML`
 * (that bypasses Quill clipboard matchers). Strip them from the fragment and return URLs for the external players.
 */
function extractMediaFromPasteHtml(html: string): { safeHtml: string; media: { url: string; mimeType: string }[] } {
  const media: { url: string; mimeType: string }[] = [];
  if (!html?.trim()) return { safeHtml: html, media };
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('video, audio').forEach((el) => {
      const tag = el.tagName.toUpperCase();
      let src =
        (el as HTMLMediaElement).getAttribute('src')?.trim() ||
        el.querySelector('source[src]')?.getAttribute('src')?.trim() ||
        '';
      if (!src) return;
      media.push({
        url: toAbsoluteMediaUrlForPlayback(src),
        mimeType: tag === 'VIDEO' ? 'video/mp4' : 'audio/mpeg',
      });
      el.remove();
    });
    const inner = doc.body?.innerHTML ?? '';
    return { safeHtml: stripVideoAudioFromHtml(inner), media };
  } catch {
    return { safeHtml: stripVideoAudioFromHtml(html), media };
  }
}

export type ExternalPlaybackRow = {
  key: string;
  url: string;
  mimeType: string;
  fileName?: string;
};

/** True when the row has plain text, an inline image, attached playback media, or staged uploads. */
function contentEditorHasSavableBody(
  html: string,
  playbackRowCount: number,
  stagedMediaCount: number,
): boolean {
  if (html.replace(/<[^>]*>/g, '').trim()) return true;
  if (/<img\b/i.test(html)) return true;
  if (playbackRowCount > 0) return true;
  if (stagedMediaCount > 0) return true;
  return false;
}

function parseExternalMediaFromAttachmentsJson(
  attachmentsJson: string | undefined,
  contentId: string | undefined,
): ExternalPlaybackRow[] {
  if (!attachmentsJson?.trim() || !contentId) return [];
  try {
    const arr = JSON.parse(attachmentsJson) as {
      id?: string;
      fileName?: string;
      mimeType?: string;
      externalAttachment?: boolean;
    }[];
    if (!Array.isArray(arr)) return [];
    const origin = getApiOrigin();
    const out: ExternalPlaybackRow[] = [];
    for (const a of arr) {
      const mime = (a.mimeType || '').toLowerCase();
      if (!a.id) continue;
      const isVideoOrAudio = mime.startsWith('video/') || mime.startsWith('audio/');
      const isExternalImage = mime.startsWith('image/') && a.externalAttachment === true;
      if (!isVideoOrAudio && !isExternalImage) continue;
      const path = `/api/content/media/${encodeURIComponent(contentId)}/${encodeURIComponent(a.id)}`;
      const absolute = origin ? `${origin}${path}` : path;
      out.push({
        key: `att:${a.id}`,
        url: withMediaAccessTokenOnUrl(absolute),
        mimeType: a.mimeType || 'application/octet-stream',
        fileName: a.fileName,
      });
    }
    return out;
  } catch {
    return [];
  }
}

/** Remove auth query params before persisting rich HTML / Delta JSON. */
function stripMediaAccessTokens(html: string): string {
  if (!html) return html;
  let out = html.replace(/([?&])access_token=[^&#"'\\s]*/gi, (_m, lead: string) => (lead === '?' ? '?' : ''));
  out = out.replace(/\?&/g, '?').replace(/\?$/, '').replace(/&&/g, '&');
  return out;
}

let narralizerSizedImageBlotRegistered = false;

/** Quill's default image embed only keeps `src` in Delta — HTML round-trip drops width. Replace with sized variant. */
function registerNarralizerSizedImageBlot() {
  if (narralizerSizedImageBlotRegistered) return;
  try {
    const BaseImage = Quill.import('formats/image') as any;

    class SizedImageBlot extends BaseImage {
      static create(value: unknown): HTMLElement {
        if (value && typeof value === 'object' && typeof (value as { src?: string }).src === 'string') {
          const v = value as { src: string; width?: string; height?: string };
          const node = super.create(v.src) as HTMLImageElement;
          if (v.width) node.setAttribute('width', String(v.width).replace(/\s*px$/i, ''));
          if (v.height) node.setAttribute('height', String(v.height).replace(/\s*px$/i, ''));
          return node;
        }
        return super.create(value as string) as HTMLImageElement;
      }

      static value(domNode: HTMLElement) {
        const img = domNode as HTMLImageElement;
        const src = img.getAttribute('src') || '';
        if (!src) return '';
        const wAttr = img.getAttribute('width');
        const hAttr = img.getAttribute('height');
        const wStyle = /^(\d+(?:\.\d+)?)px$/i.exec(img.style.width || '');
        const hStyle = /^(\d+(?:\.\d+)?)px$/i.exec(img.style.height || '');
        const w = wAttr || (wStyle && wStyle[1]);
        const h = hAttr || (hStyle && hStyle[1]);
        if (w || h) {
          return {
            src,
            ...(w ? { width: String(w) } : {}),
            ...(h ? { height: String(h) } : {}),
          };
        }
        return src;
      }
    }

    SizedImageBlot.blotName = BaseImage.blotName;
    SizedImageBlot.tagName = BaseImage.tagName;
    if (BaseImage.className) SizedImageBlot.className = BaseImage.className;

    Quill.register(SizedImageBlot, true);
    narralizerSizedImageBlotRegistered = true;
  } catch {
    /* Quill already patched or import failed */
  }
}

registerNarralizerSizedImageBlot();

// Add type definitions for Web Speech API
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionError) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionError extends Event {
  error: string;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

// Extend Window interface to include webkitSpeechRecognition
declare global {
  interface Window {
    webkitSpeechRecognition: {
      new (): SpeechRecognition;
    };
  }
}

function isRowPersistedOrStagedMedia(row: ExternalPlaybackRow): boolean {
  return (
    row.key.startsWith('att:') || row.key.startsWith('st:') || row.key.startsWith('opt:')
  );
}

function mediaAttachmentSubtitle(row: ExternalPlaybackRow): string {
  const mime = (row.mimeType || '').toLowerCase();
  const subtype = mime.includes('/') ? (mime.split('/')[1] || '').toUpperCase() : '';
  if (mime.startsWith('image/')) {
    return subtype ? `Image · ${subtype}` : 'Image file';
  }
  const isVideo = mime.startsWith('video/');
  if (subtype) return `${isVideo ? 'Video' : 'Audio'} · ${subtype}`;
  return isVideo ? 'Video file' : 'Audio file';
}

type AttachedMediaMenuBodyProps = {
  rows: ExternalPlaybackRow[];
  keyPrefix: string;
  readOnly: boolean;
  mediaDeletingKey: string | null;
  onPlay: (row: ExternalPlaybackRow) => void;
  onDeleteRow?: (row: ExternalPlaybackRow) => void;
};

function AttachedMediaMenuBody({
  rows,
  keyPrefix,
  readOnly,
  mediaDeletingKey,
  onPlay,
  onDeleteRow,
}: AttachedMediaMenuBodyProps) {
  const theme = useTheme();
  if (!rows.length) return null;
  return (
    <Box
      component="section"
      aria-label="Attached media (video, audio, and external images)"
      sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.08),
        }}
      >
        <PermMediaOutlinedIcon color="primary" fontSize="small" aria-hidden />
        <Typography component="h3" variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
          Attached media
        </Typography>
        <Chip size="small" label={rows.length} variant="outlined" sx={{ height: 22, fontWeight: 600 }} />
      </Box>
      <List dense disablePadding component="ul">
        {rows.map((row, index) => {
          const mimeLower = row.mimeType.toLowerCase();
          const isVideo = mimeLower.startsWith('video/');
          const isImage = mimeLower.startsWith('image/');
          const title =
            row.fileName || (isImage ? 'Image' : isVideo ? 'Video clip' : 'Audio clip');
          const openLabel = isImage ? 'View' : 'Play';
          const OpenIcon = isImage ? ImageIcon : PlayArrowIcon;
          return (
            <ListItem
              key={`${keyPrefix}-${row.key}`}
              component="li"
              alignItems="flex-start"
              divider={index < rows.length - 1}
              sx={{
                py: 1.25,
                px: 2,
                pr: 1,
                '& .MuiListItemText-root': { pr: 1, minWidth: 0 },
              }}
              secondaryAction={
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ my: 0.5 }}>
                  {!readOnly && onDeleteRow && isRowPersistedOrStagedMedia(row) && (
                    <Tooltip title="Remove from this content">
                      <span>
                        <IconButton
                          size="small"
                          edge="end"
                          color="error"
                          disabled={mediaDeletingKey === row.key}
                          onClick={() => onDeleteRow(row)}
                          aria-label="Remove attachment"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title={isImage ? 'Open image' : 'Open in viewer'}>
                    <Button
                      className="content-media-play-button"
                      variant="contained"
                      size="small"
                      disableElevation
                      startIcon={<OpenIcon />}
                      onClick={() => onPlay(row)}
                      sx={{ minWidth: 96 }}
                    >
                      {openLabel}
                    </Button>
                  </Tooltip>
                </Stack>
              }
            >
              <ListItemIcon sx={{ minWidth: 52, mt: 0.25 }}>
                <Avatar
                  variant="rounded"
                  sx={{
                    width: 44,
                    height: 44,
                    bgcolor: isImage
                      ? alpha(theme.palette.success.main, 0.85)
                      : isVideo
                        ? alpha(theme.palette.primary.main, 0.88)
                        : alpha(theme.palette.secondary.main, 0.75),
                    color: 'common.white',
                  }}
                  aria-hidden
                >
                  {isImage ? (
                    <ImageIcon sx={{ fontSize: 22 }} />
                  ) : isVideo ? (
                    <VideocamIcon sx={{ fontSize: 22 }} />
                  ) : (
                    <AudioFileIcon sx={{ fontSize: 22 }} />
                  )}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={title}
                secondary={mediaAttachmentSubtitle(row)}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: 600,
                  title,
                  sx: {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    lineHeight: 1.35,
                  },
                }}
                secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}

type AttachedMediaPopoverProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  rows: ExternalPlaybackRow[];
  keyPrefix: string;
  readOnly: boolean;
  mediaDeletingKey: string | null;
  onMediaPlay: (row: ExternalPlaybackRow) => void;
  onDeleteRow?: (row: ExternalPlaybackRow) => void;
};

function AttachedMediaPopover({
  anchorEl,
  onClose,
  rows,
  keyPrefix,
  readOnly,
  mediaDeletingKey,
  onMediaPlay,
  onDeleteRow,
}: AttachedMediaPopoverProps) {
  const open = Boolean(anchorEl) && rows.length > 0;
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        elevation: 8,
        sx: {
          mt: 0.75,
          width: 'min(560px, calc(100vw - 32px))',
          minWidth: 400,
          maxHeight: 'min(72vh, 520px)',
          overflow: 'auto',
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        },
      }}
    >
      <AttachedMediaMenuBody
        rows={rows}
        keyPrefix={keyPrefix}
        readOnly={readOnly}
        mediaDeletingKey={mediaDeletingKey}
        onPlay={(row) => {
          onMediaPlay(row);
          onClose();
        }}
        onDeleteRow={onDeleteRow}
      />
    </Popover>
  );
}

interface ContentEditorProps {
  subcases: string[];
  /** When true, subcase must be chosen from `subcases` (no free-text). */
  subcaseSelectOnly?: boolean;
  initialSubcase?: string;
  initialText?: string;
  category?: Category | null;
  contentId?: string | undefined;
  /** Server-side attachment list (video/audio shown below the editor, not in the body). */
  attachmentsJson?: string | undefined;
  readOnly?: boolean;
  /** Called after a persisted attachment is removed so the parent can reload `attachmentsJson`. */
  onMediaAttachmentsChanged?: () => void;
  onSave: (subcase: string, plainText: string, richTextHtml: string, richTextDelta: string, stagedMediaIds: string[]) => void;
  onCancel: () => void;
  /** Keeps the panel "Select Subcase" in sync while editing (free solo / autocomplete). */
  onSubcaseChange?: (subcase: string) => void;
  highlightedText?: string;
  searchTerms?: string[];
  searchMode?: 'any' | 'all' | 'exact' | 'semantic';
  wholeWord?: boolean;
  caseSensitive?: boolean;
  showWarning?: boolean;
}

export type ContentEditorHandle = {
  /** Live Quill HTML for full-screen preview (includes unsaved edits). */
  getRichHtmlForPreview: () => string;
};

const EditorContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '16px',
  overflow: 'auto',
  height: '100%',
});

const TopBar = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
});

const EditorWrapper = styled(Box)({
  flex: 1,
  minHeight: 0,
  '& .quill': {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  '& .ql-container': {
    flex: 1,
    overflow: 'auto',
  },
  '& .ql-editor': {
    minHeight: '200px',
  },
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const LanguageSelect = styled(FormControl)(({ theme }) => ({
  minWidth: 120,
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  marginTop: theme.spacing(1),
  marginLeft: theme.spacing(1),
}));

const WarningBanner = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.warning.light,
  color: theme.palette.warning.contrastText,
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  border: `1px solid ${theme.palette.warning.main}`,
}));

// Custom styles for highlighting
function normalizeSpeechTranscript(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

const highlightStyles = `
  .ql-editor .ql-background {
    background-color: yellow !important;
  }
  .ql-editor .ql-color {
    color: black !important;
  }
  .ql-editor [style*="background: yellow"] {
    background-color: yellow !important;
    color: black !important;
  }
  .ql-editor [style*="background-color: yellow"] {
    background-color: yellow !important;
    color: black !important;
  }
`;

/** Beats Quill Snow \`.ql-editor { text-align: left }\` when enable() runs on edit. */
const editorRtlStyles = `
  .narralizer-editor-rtl .ql-container .ql-editor {
    direction: rtl !important;
    text-align: right !important;
  }
  .narralizer-editor-rtl .ql-container .ql-editor p,
  .narralizer-editor-rtl .ql-container .ql-editor ol,
  .narralizer-editor-rtl .ql-container .ql-editor ul,
  .narralizer-editor-rtl .ql-container .ql-editor pre,
  .narralizer-editor-rtl .ql-container .ql-editor blockquote,
  .narralizer-editor-rtl .ql-container .ql-editor h1,
  .narralizer-editor-rtl .ql-container .ql-editor h2,
  .narralizer-editor-rtl .ql-container .ql-editor h3,
  .narralizer-editor-rtl .ql-container .ql-editor h4,
  .narralizer-editor-rtl .ql-container .ql-editor h5,
  .narralizer-editor-rtl .ql-container .ql-editor h6 {
    text-align: right !important;
  }
  .narralizer-editor-rtl .ql-container .ql-editor .ql-align-center {
    text-align: center !important;
  }
  .narralizer-editor-rtl .ql-container .ql-editor [style*="text-align: left"],
  .narralizer-editor-rtl .ql-container .ql-editor [style*="text-align:left"] {
    text-align: right !important;
  }
`;

function applyQuillRootRtlLayout(root: HTMLElement | null | undefined): void {
  if (!root) return;
  root.setAttribute('dir', 'rtl');
  root.style.setProperty('direction', 'rtl', 'important');
  root.style.setProperty('text-align', 'right', 'important');
}

function clearQuillRootRtlLayout(root: HTMLElement | null | undefined): void {
  if (!root) return;
  root.removeAttribute('dir');
  root.style.removeProperty('direction');
  root.style.removeProperty('text-align');
}

const ContentEditor = forwardRef<ContentEditorHandle, ContentEditorProps>(({
  subcases,
  subcaseSelectOnly = false,
  initialSubcase = '',
  initialText = '',
  category,
  contentId,
  attachmentsJson,
  readOnly = false,
  onMediaAttachmentsChanged,
  onSave,
  onCancel,
  onSubcaseChange,
  highlightedText,
  searchTerms,
  searchMode = 'exact',
  wholeWord = false,
  caseSensitive = false,
  showWarning = false,
}, ref) => {
  
  const [subcase, setSubcase] = useState(initialSubcase);
  const [text, setText] = useState(initialText);
  const contentIsRtl = useMemo(
    () => contentTextLooksRtl(initialText || text || ''),
    [text, initialText],
  );
  const [error, setError] = useState('');
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isStartingRecording, setIsStartingRecording] = useState(false);
  const [expandedEditorOpen, setExpandedEditorOpen] = useState(false);
  const [mediaInsertNotice, setMediaInsertNotice] = useState<string | null>(null);
  /** Shown while a media file is uploading to staging or content (multipart progress). */
  const [mediaUploadBar, setMediaUploadBar] = useState<
    null | { variant: 'determinate'; value: number } | { variant: 'indeterminate' }
  >(null);
  /** In-tab modal player (same browser tab as the editor). */
  const [mediaPlayerRow, setMediaPlayerRow] = useState<ExternalPlaybackRow | null>(null);
  const [mediaViewerFullScreen, setMediaViewerFullScreen] = useState(false);
  /** Dialog Paper ref — `resize: both` sets inline width/height; strip them when entering fullscreen. */
  const mediaViewerPaperRef = useRef<HTMLDivElement | null>(null);
  const recordingDidStartRef = useRef(false);
  const pendingStartRef = useRef(false);
  const autoRetryRef = useRef(false);
  const lastInterimTranscriptRef = useRef('');
  /** Latest full transcript from the recognition engine (all result segments). */
  const lastFullTranscriptRef = useRef('');
  /** Cumulative transcript already inserted into Quill during this speech session. */
  const speechAppendedTranscriptRef = useRef('');
  const recordingStopRequestedRef = useRef(false);
  const speechSessionActiveRef = useRef(false);
  const speechFinalizeTimerRef = useRef<number | null>(null);
  /** Range of characters inserted during the current speech session (for Quill align after stop). */
  const speechSessionRangeRef = useRef<{ start: number | null; endExclusive: number | null }>({
    start: null,
    endExclusive: null,
  });
  const isRecordingRef = useRef(false);
  const appendTranscriptToQuillRef = useRef<(t: string) => void>(() => {});
  const commitPendingSpeechTranscriptRef = useRef<() => void>(() => {});
  const applySpeechSessionFormattingRef = useRef<() => void>(() => {});
  const audioCtxRef = useRef<AudioContext | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    // Load default language from localStorage, fallback to 'en-US'
    const saved = localStorage.getItem('defaultSpeechLanguage');
    return saved || 'en-US';
  });
  const selectedLanguageRef = useRef(selectedLanguage);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const quillRef = useRef<any>(null);
  /** Quill loses selection when the file picker opens; keep last caret for insertEmbed. */
  const lastQuillCaretRef = useRef<{ index: number; length: number } | null>(null);
  const stagedMediaIdsRef = useRef<string[]>([]);

  const discardStagedMediaOnServer = useCallback((ids: string[]) => {
    const unique = Array.from(new Set(ids.filter((id) => typeof id === 'string' && id.trim().length > 0)));
    if (unique.length === 0) return;
    void Promise.allSettled(
      unique.map((id) => api.delete(`/content/media-staging/${encodeURIComponent(id)}`)),
    );
  }, []);

  const mainMediaMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const expandedMediaMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const [attachedMediaAnchorEl, setAttachedMediaAnchorEl] = useState<HTMLElement | null>(null);
  const [expandedMediaAnchorEl, setExpandedMediaAnchorEl] = useState<HTMLElement | null>(null);
  const appendExternalPlaybackRef = useRef<(row: ExternalPlaybackRow) => void>(() => {});
  const [extraPlaybackRows, setExtraPlaybackRows] = useState<ExternalPlaybackRow[]>([]);
  const [mediaDeletingKey, setMediaDeletingKey] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  /** Quill toolbar image uses the same file input but always embeds; toolbar Image button respects the switch. */
  const imageInsertForceEmbedRef = useRef(false);
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  const audioInputRef = useRef<HTMLInputElement | null>(null);
  const insertMediaAtCursorRef = useRef<(file: File, opts?: { forceEmbedImage?: boolean }) => void>(() => {});
  const { getToolbarTooltipTextForState } = useHelpContent();
  const theme = useTheme();

  useEffect(() => {
    if (!mediaInsertNotice) return;
    const t = window.setTimeout(() => setMediaInsertNotice(null), 5000);
    return () => window.clearTimeout(t);
  }, [mediaInsertNotice]);

  const languages = [
    { code: 'en-US', name: 'English (US)' },
    { code: 'he-IL', name: 'Hebrew' },
    { code: 'es-ES', name: 'Spanish' },
    { code: 'fr-FR', name: 'French' },
    { code: 'de-DE', name: 'German' },
    { code: 'it-IT', name: 'Italian' },
    { code: 'pt-BR', name: 'Portuguese' },
    { code: 'ru-RU', name: 'Russian' },
    { code: 'ja-JP', name: 'Japanese' },
    { code: 'ko-KR', name: 'Korean' },
    { code: 'zh-CN', name: 'Chinese' },
  ];

  const isRtlSpeechLanguage = /^(he|ar|fa|ur)(-|$)/i.test(selectedLanguage);

  const editorRtlLayout = useMemo(
    () => contentIsRtl || (isRecording && isRtlSpeechLanguage),
    [contentIsRtl, isRecording, isRtlSpeechLanguage],
  );

  const syncQuillRtlLayout = useCallback(() => {
    const root = quillRef.current?.getEditor?.()?.root as HTMLElement | undefined;
    if (!root) return;
    if (editorRtlLayout) {
      applyQuillRootRtlLayout(root);
    } else {
      clearQuillRootRtlLayout(root);
    }
  }, [editorRtlLayout]);

  useEffect(() => {
    selectedLanguageRef.current = selectedLanguage;
  }, [selectedLanguage]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  useImperativeHandle(ref, () => ({
    getRichHtmlForPreview: () => {
      const quill = quillRef.current?.getEditor?.();
      const raw = quill ? (quill.root.innerHTML as string) : text;
      return stripVideoAudioFromHtml(raw);
    },
  }), [text]);

  useEffect(() => {
    appendExternalPlaybackRef.current = (row) => {
      setExtraPlaybackRows((prev) => {
        if (prev.some((p) => p.url === row.url)) return prev;
        return [...prev, row];
      });
    };
  }, []);

  const serverPlaybackRows = useMemo(
    () => parseExternalMediaFromAttachmentsJson(attachmentsJson, contentId),
    [attachmentsJson, contentId],
  );

  const [embedImageInEditor, setEmbedImageInEditor] = useState(() => {
    try {
      const v = localStorage.getItem('narralizer-embed-images-in-body');
      if (v === 'false') return false;
    } catch {
      /* ignore */
    }
    return true;
  });

  useEffect(() => {
    try {
      localStorage.setItem('narralizer-embed-images-in-body', embedImageInEditor ? 'true' : 'false');
    } catch {
      /* ignore */
    }
  }, [embedImageInEditor]);

  const allPlaybackRows = useMemo(() => {
    const seen = new Set<string>();
    const out: ExternalPlaybackRow[] = [];
    for (const r of [...serverPlaybackRows, ...extraPlaybackRows]) {
      if (seen.has(r.url)) continue;
      seen.add(r.url);
      out.push(r);
    }
    return out;
  }, [serverPlaybackRows, extraPlaybackRows]);

  useEffect(() => {
    if (allPlaybackRows.length === 0) {
      setAttachedMediaAnchorEl(null);
      setExpandedMediaAnchorEl(null);
    }
  }, [allPlaybackRows.length]);

  useEffect(() => {
    if (!mediaPlayerRow) {
      setMediaViewerFullScreen(false);
    }
  }, [mediaPlayerRow]);

  useLayoutEffect(() => {
    if (!mediaViewerFullScreen) return;
    const clearResizeInlineStyles = () => {
      const el = mediaViewerPaperRef.current;
      if (!el) return;
      el.style.removeProperty('width');
      el.style.removeProperty('height');
      el.style.removeProperty('max-width');
      el.style.removeProperty('max-height');
      el.style.removeProperty('min-width');
      el.style.removeProperty('min-height');
    };
    clearResizeInlineStyles();
    const id = requestAnimationFrame(clearResizeInlineStyles);
    return () => cancelAnimationFrame(id);
  }, [mediaViewerFullScreen]);

  /** Inline images: select + drag handle to resize; double-click image to clear size. */
  useEffect(() => {
    if (readOnly) return undefined;

    let disposed = false;
    let pendingRaf = 0;
    let editorRootEl: HTMLElement | null = null;
    let selectedImg: HTMLImageElement | null = null;
    let handleEl: HTMLDivElement | null = null;

    const removeSelectionUI = () => {
      if (selectedImg) {
        selectedImg.classList.remove('narralizer-img-selected');
        selectedImg = null;
      }
      if (handleEl) {
        handleEl.remove();
        handleEl = null;
      }
    };

    const syncHtmlFromQuill = () => {
      const quill = quillRef.current?.getEditor?.();
      if (!quill) return;
      const html = quill.root.innerHTML as string;
      try {
        const delta = quill.clipboard.convert(html);
        quill.setContents(delta, 'silent');
      } catch {
        /* keep DOM if convert fails */
      }
      setText(quill.root.innerHTML as string);
    };

    const updateHandlePosition = () => {
      if (!selectedImg || !handleEl) return;
      const r = selectedImg.getBoundingClientRect();
      handleEl.style.left = `${r.right - 7}px`;
      handleEl.style.top = `${r.bottom - 7}px`;
    };

    const selectImage = (img: HTMLImageElement) => {
      removeSelectionUI();
      selectedImg = img;
      img.classList.add('narralizer-img-selected');
      const h = document.createElement('div');
      h.setAttribute('aria-label', 'Drag to resize image');
      Object.assign(h.style, {
        position: 'fixed',
        zIndex: '2000',
        width: '14px',
        height: '14px',
        boxSizing: 'border-box',
        left: '0',
        top: '0',
        borderRadius: '2px',
        backgroundColor: theme.palette.primary.main,
        border: `2px solid ${theme.palette.common.white}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
        cursor: 'nwse-resize',
        touchAction: 'none',
      });
      document.body.appendChild(h);
      handleEl = h;
      updateHandlePosition();

      const onHandleMouseDown = (downEv: MouseEvent) => {
        downEv.preventDefault();
        downEv.stopPropagation();
        const imgEl = selectedImg;
        if (!imgEl) return;
        const startX = downEv.clientX;
        const startW = imgEl.getBoundingClientRect().width;
        const quill = quillRef.current?.getEditor?.();
        const root = quill?.root as HTMLElement | undefined;
        const maxW = root ? root.clientWidth : 1200;

        const onMove = (moveEv: MouseEvent) => {
          if (!selectedImg) return;
          const dw = moveEv.clientX - startX;
          const next = Math.round(Math.max(40, Math.min(startW + dw, maxW)));
          selectedImg.setAttribute('width', String(next));
          selectedImg.style.width = '';
          selectedImg.style.height = 'auto';
          selectedImg.style.maxWidth = '100%';
          updateHandlePosition();
        };
        const onUp = () => {
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onUp);
          syncHtmlFromQuill();
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
      };
      h.addEventListener('mousedown', onHandleMouseDown);
    };

    const onDocMouseDownCapture = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (handleEl && (t === handleEl || handleEl.contains(t))) return;
      const rootNow = quillRef.current?.getEditor?.()?.root as HTMLElement | undefined;
      if (!rootNow) return;
      if (t.tagName === 'IMG' && rootNow.contains(t)) {
        e.preventDefault();
        selectImage(t as HTMLImageElement);
        return;
      }
      removeSelectionUI();
    };

    const onEditorDblClickCapture = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t || t.tagName !== 'IMG') return;
      if (!editorRootEl || !editorRootEl.contains(t)) return;
      e.preventDefault();
      const im = t as HTMLImageElement;
      im.removeAttribute('width');
      im.removeAttribute('height');
      im.style.width = '';
      im.style.height = '';
      removeSelectionUI();
      syncHtmlFromQuill();
    };

    const onWinScrollOrResize = () => updateHandlePosition();

    let attempts = 0;
    const bindToEditor = () => {
      if (disposed) return;
      if (attempts++ > 200) return;
      const r = quillRef.current?.getEditor?.()?.root as HTMLElement | undefined;
      if (!r) {
        pendingRaf = requestAnimationFrame(bindToEditor);
        return;
      }
      editorRootEl = r;
      document.addEventListener('mousedown', onDocMouseDownCapture, true);
      editorRootEl.addEventListener('dblclick', onEditorDblClickCapture, true);
      window.addEventListener('resize', onWinScrollOrResize);
      window.addEventListener('scroll', onWinScrollOrResize, true);
    };

    bindToEditor();

    return () => {
      disposed = true;
      cancelAnimationFrame(pendingRaf);
      removeSelectionUI();
      document.removeEventListener('mousedown', onDocMouseDownCapture, true);
      if (editorRootEl) {
        editorRootEl.removeEventListener('dblclick', onEditorDblClickCapture, true);
        editorRootEl = null;
      }
      window.removeEventListener('resize', onWinScrollOrResize);
      window.removeEventListener('scroll', onWinScrollOrResize, true);
    };
  }, [readOnly, expandedEditorOpen, theme.palette.primary.main, theme.palette.common.white, setText]);

  useEffect(() => {
    if (expandedEditorOpen) setAttachedMediaAnchorEl(null);
    else setExpandedMediaAnchorEl(null);
  }, [expandedEditorOpen]);

  useEffect(() => {
    const toDiscard = [...stagedMediaIdsRef.current];
    stagedMediaIdsRef.current = [];
    setExtraPlaybackRows([]);
    discardStagedMediaOnServer(toDiscard);
  }, [contentId, discardStagedMediaOnServer]);

  const appendTranscriptToQuill = useCallback((transcript: string) => {
    const cleaned = (transcript || '').trim();
    if (!cleaned) return;

    const quill = quillRef.current?.getEditor?.();
    if (!quill) {
      // Fallback: best-effort append to the current HTML string
      setText(prev => prev + (prev ? ' ' : '') + cleaned);
      return;
    }

    // For speech-to-text we always append at the end so we don't overwrite/insert into the middle
    // of existing content. When editing existing content, start a new line before appending.
    const endIndex = Math.max(0, quill.getLength() - 1); // exclude Quill's trailing newline
    const hasExisting = quill.getText(0, endIndex).trim().length > 0;
    const lastChar = endIndex > 0 ? quill.getText(endIndex - 1, 1) : '';
    const needsNewLine = hasExisting && lastChar !== '\n';
    const insertText = `${needsNewLine ? '\n' : ''}${cleaned}`;
    const insertStart = endIndex;

    quill.insertText(endIndex, insertText, 'user');
    const afterEnd = endIndex + insertText.length;
    if (speechSessionActiveRef.current) {
      if (speechSessionRangeRef.current.start === null) {
        speechSessionRangeRef.current.start = insertStart;
      }
      speechSessionRangeRef.current.endExclusive = afterEnd;
    }
    quill.setSelection(afterEnd, 0, 'silent');
    setText(quill.root.innerHTML);
  }, []);

  const applySpeechSessionFormatting = useCallback(() => {
    const quill = quillRef.current?.getEditor?.();
    const { start, endExclusive } = speechSessionRangeRef.current;
    speechSessionRangeRef.current = { start: null, endExclusive: null };
    if (!quill || start == null || endExclusive == null || endExclusive <= start) {
      return;
    }

    const len = endExclusive - start;
    const rtl = /^(he|ar|fa|ur)(-|$)/i.test(selectedLanguageRef.current);
    if (rtl) {
      quill.formatLine(start, len, 'align', 'right', 'user');
    }

    quill.insertText(quill.getLength() - 1, '\n', 'user');
    quill.setSelection(quill.getLength() - 1, 0, 'silent');
    quill.format('align', false, 'user');
    setText(quill.root.innerHTML);
  }, []);

  appendTranscriptToQuillRef.current = appendTranscriptToQuill;
  applySpeechSessionFormattingRef.current = applySpeechSessionFormatting;

  const commitPendingSpeechTranscript = useCallback(() => {
    const full = normalizeSpeechTranscript(
      lastFullTranscriptRef.current || lastInterimTranscriptRef.current || ''
    );
    if (!full) {
      lastInterimTranscriptRef.current = '';
      return;
    }

    const appended = normalizeSpeechTranscript(speechAppendedTranscriptRef.current);
    if (full === appended) {
      lastInterimTranscriptRef.current = '';
      lastFullTranscriptRef.current = full;
      return;
    }

    let toAppend = '';
    if (!appended) {
      toAppend = full;
    } else if (full.startsWith(appended)) {
      toAppend = full.slice(appended.length).trim();
    } else if (!appended.includes(full)) {
      toAppend = full;
    }

    if (toAppend) {
      appendTranscriptToQuillRef.current(toAppend);
    }
    speechAppendedTranscriptRef.current = full;
    lastInterimTranscriptRef.current = '';
    lastFullTranscriptRef.current = full;
  }, []);

  commitPendingSpeechTranscriptRef.current = commitPendingSpeechTranscript;

  // Sync subcase from props (panel dropdown ↔ editor field) without touching body text.
  useEffect(() => {
    setSubcase(initialSubcase);
  }, [initialSubcase]);

  // Reload editor body when opening a different row or parent refreshes saved content.
  useEffect(() => {
    const raw = initialText || '';
    if (raw.trim().startsWith('{')) {
      setText(raw);
    } else {
      const prepared = prepareContentRichPreviewHtml(raw);
      setText(stripVideoAudioFromHtml(prepared));
    }
  }, [initialText]);

  // Initialize speech recognition (avoid tying this to `initialText`, which may change during editing)
  useEffect(() => {
    // Lazily initialize in toggleRecording; here we only ensure language stays in sync.
    if (recognitionRef.current) {
      recognitionRef.current.lang = selectedLanguage;
    }
  }, [selectedLanguage]);

  const playStartBeep = async () => {
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioCtx();
      }
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.value = 0.1;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      window.setTimeout(() => {
        try {
          osc.stop();
        } catch (_) {
          // ignore
        }
      }, 180);
    } catch (_) {
      // ignore
    }
  };

  const ensureRecognition = (): SpeechRecognition | null => {
    if (recognitionRef.current) return recognitionRef.current;
    if (!('webkitSpeechRecognition' in window)) {
      recognitionRef.current = null;
      return null;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = selectedLanguage;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const results = event.results;
      let finalText = '';
      let interimText = '';
      let fullTranscript = '';

      for (let i = 0; i < results.length; i++) {
        const r = results[i];
        const t = r?.[0]?.transcript || '';
        if (t) fullTranscript += t;
      }

      for (let i = (event as any).resultIndex ?? 0; i < results.length; i++) {
        const r = results[i];
        const t = r?.[0]?.transcript || '';
        if (!t) continue;
        if (r.isFinal) finalText += t + ' ';
        else interimText += t + ' ';
      }

      if (fullTranscript.trim()) {
        lastFullTranscriptRef.current = normalizeSpeechTranscript(fullTranscript);
      }

      const cleanedInterim = interimText.trim();
      if (cleanedInterim) {
        lastInterimTranscriptRef.current = cleanedInterim;
      }

      if (recordingStopRequestedRef.current) {
        return;
      }

      const cleanedFinal = finalText.trim();
      if (cleanedFinal) {
        lastInterimTranscriptRef.current = '';
        appendTranscriptToQuillRef.current(cleanedFinal);
        const prev = normalizeSpeechTranscript(speechAppendedTranscriptRef.current);
        speechAppendedTranscriptRef.current = normalizeSpeechTranscript(
          prev ? `${prev} ${cleanedFinal}` : cleanedFinal
        );
        lastFullTranscriptRef.current = speechAppendedTranscriptRef.current;
      }
    };

    (recognition as any).onstart = () => {
      recordingDidStartRef.current = true;
      pendingStartRef.current = false;
      autoRetryRef.current = false;
      lastInterimTranscriptRef.current = '';
      lastFullTranscriptRef.current = '';
      speechAppendedTranscriptRef.current = '';
      recordingStopRequestedRef.current = false;
      speechSessionActiveRef.current = true;
      if (speechFinalizeTimerRef.current != null) {
        window.clearTimeout(speechFinalizeTimerRef.current);
        speechFinalizeTimerRef.current = null;
      }
      speechSessionRangeRef.current = { start: null, endExclusive: null };
      setIsStartingRecording(false);
      void playStartBeep();
      setIsRecording(true);
      // Make readiness visible: focus the editor and show a caret.
      try {
        const quill = quillRef.current?.getEditor?.();
        if (quill) {
          quill.focus();
          const end = quill.getLength();
          quill.setSelection(end, 0, 'silent');
        }
      } catch (_) {
        // ignore
      }
    };

    recognition.onerror = (event: SpeechRecognitionError) => {
      console.error('Speech recognition error:', event.error);
      pendingStartRef.current = false;
      speechSessionActiveRef.current = false;
      setIsStartingRecording(false);
      setIsRecording(false);
    };

    recognition.onend = () => {
      // First-use quirk: start() can return but recognition immediately ends while the
      // browser prompts for permission. If that happens, retry once automatically.
      if (pendingStartRef.current && !recordingDidStartRef.current && !autoRetryRef.current) {
        autoRetryRef.current = true;
        pendingStartRef.current = false;
        try {
          recognition.start();
          pendingStartRef.current = true;
          return;
        } catch (e) {
          console.error('Auto-retry speech recognition start failed:', e);
        }
      }

      if (speechFinalizeTimerRef.current != null) {
        window.clearTimeout(speechFinalizeTimerRef.current);
      }
      // Late onresult events can arrive after stop(); wait briefly before final commit.
      speechFinalizeTimerRef.current = window.setTimeout(() => {
        speechFinalizeTimerRef.current = null;
        commitPendingSpeechTranscriptRef.current();
        applySpeechSessionFormattingRef.current();
        speechSessionActiveRef.current = false;
        pendingStartRef.current = false;
        recordingStopRequestedRef.current = false;
        setIsStartingRecording(false);
        setIsRecording(false);
      }, 300);
    };

    recognitionRef.current = recognition;
    return recognition;
  };

  const ensureMicPermission = async (): Promise<boolean> => {
    // Web Speech API permission behavior is inconsistent. On first use, the browser permission prompt
    // can delay/interrupt recognition start and the first utterance is often lost. Requesting getUserMedia
    // first forces the prompt and stabilizes first-time transcription.
    if (!navigator.mediaDevices?.getUserMedia) return true;
    try {
      if (!micStreamRef.current) {
        micStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      return true;
    } catch (e) {
      console.error('Failed to acquire microphone permission:', e);
      return false;
    }
  };

  const stopMicStream = () => {
    try {
      const s = micStreamRef.current;
      if (s) {
        s.getTracks().forEach(t => t.stop());
      }
    } catch (_) {
      // ignore
    } finally {
      micStreamRef.current = null;
    }
  };

  useEffect(() => {
    if (readOnly) {
      setExpandedEditorOpen(false);
    }
  }, [readOnly]);

  // Quill enable() on edit clears dir / text-align; re-apply before paint and on every editor mutation.
  useLayoutEffect(() => {
    syncQuillRtlLayout();
  }, [syncQuillRtlLayout, readOnly, text, initialText]);

  useEffect(() => {
    if (!editorRtlLayout) return undefined;
    let quillInstance: ReturnType<NonNullable<typeof quillRef.current>['getEditor']> | null = null;
    let root: HTMLElement | null = null;
    let cancelled = false;
    const reapply = () => {
      if (root) applyQuillRootRtlLayout(root);
    };
    const attach = () => {
      if (cancelled) return;
      const quill = quillRef.current?.getEditor?.();
      if (!quill) {
        requestAnimationFrame(attach);
        return;
      }
      quillInstance = quill;
      root = quill.root as HTMLElement;
      reapply();
      quill.on('editor-change', reapply);
      root.addEventListener('focus', reapply, true);
    };
    attach();
    return () => {
      cancelled = true;
      if (quillInstance && root) {
        quillInstance.off('editor-change', reapply);
        root.removeEventListener('focus', reapply, true);
      }
    };
  }, [editorRtlLayout, readOnly]);

  // Effect to handle text highlighting
  useEffect(() => {

    // While actively editing, don't run highlight formatting passes.
    // Quill formatting calls can steal/alter the current selection and make paste/newline behavior flaky.
    if (isEditing) return;

    if (highlightedText && highlightedText.trim() && searchTerms && searchTerms.length > 0) {
      // Add a longer delay to ensure the editor is fully loaded and text is set
      const timer = setTimeout(() => {
        if (quillRef.current) {
          const quill = quillRef.current.getEditor();

          if (quill) {

          }
        }
        highlightTextInEditor(searchTerms || [], searchMode, wholeWord, caseSensitive);
      }, 500); // Increased delay to ensure editor is ready
      return () => clearTimeout(timer);
    } else {

      // Don't clear highlights when there's no highlightedText to avoid clearing content
    }
  }, [highlightedText, initialText, searchTerms, searchMode, wholeWord, caseSensitive, isEditing]);

  const highlightTextInEditor = (searchTerms: string[], searchMode: 'any' | 'all' | 'exact' | 'semantic', wholeWord: boolean, caseSensitive: boolean) => {
    
    if (!quillRef.current) {
      return;
    }

    const quill = quillRef.current.getEditor();
    if (!quill) {
      return;
    }

    // Preserve cursor/selection while we apply formatting highlights.
    const selection = quill.getSelection();

    // Clear existing highlights first
    clearHighlights(selection);

    // Get the text content from Quill
    const text = quill.getText();
    console.log('Editor text:', text);
    
    // Determine which terms to highlight based on search mode
    let termsToHighlight: string[] = [];
    
    if (searchMode === 'exact') {
      // For exact mode, highlight the entire search string
      termsToHighlight = [searchTerms.join(' ')];
    } else {
      // For 'any' and 'all' modes, highlight individual terms
      termsToHighlight = searchTerms;
    }

    
    // Find all occurrences of each term and highlight them
    const allMatches: Array<{index: number, length: number}> = [];
    
    termsToHighlight.forEach(term => {
      
      if (wholeWord) {
        // Unicode-aware word boundary
        const escapedPattern = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const wordBoundary = new RegExp(`(?<=^|[^\\p{L}\\p{N}])${escapedPattern}(?=[^\\p{L}\\p{N}]|$)`, caseSensitive ? 'gu' : 'giu');
        let match;
        let matchCount = 0;
        const maxMatches = 100; // Safety limit
        while ((match = wordBoundary.exec(text)) !== null && matchCount < maxMatches) {
          allMatches.push({
            index: match.index,
            length: match[0].length
          });
          matchCount++;
        }
        if (matchCount >= maxMatches) {
          console.warn('Reached maximum match limit for term:', term);
        }

      } else {
        // For partial matches, use simple regex
        const searchRegex = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), caseSensitive ? 'g' : 'gi');
        
        let match;
        let matchCount = 0;
        const maxMatches = 100; // Safety limit
        
        while ((match = searchRegex.exec(text)) !== null && matchCount < maxMatches) {
          
          allMatches.push({
            index: match.index,
            length: match[0].length
          });
          matchCount++;
        }
        
        if (matchCount >= maxMatches) {
          console.warn('Reached maximum match limit for term:', term);
        }
      }
    });


    // Sort matches by index to avoid conflicts when applying highlights
    allMatches.sort((a, b) => a.index - b.index);

    // Apply highlighting to each match using Quill's formatText
    allMatches.forEach(({index, length}) => {
      try {
        console.log(`Attempting to highlight text at index ${index}, length ${length}`);
        quill.formatText(index, length, {
          'background': 'yellow'
        });
        console.log(`Successfully highlighted text at index ${index}, length ${length}`);
      } catch (error) {
        console.error('Error highlighting text:', error);
      }
    });

    // Restore selection so paste/newline keeps working at the caret.
    if (selection) {
      try {
        quill.setSelection(selection.index, selection.length, 'silent');
      } catch {
        // ignore
      }
    }

    console.log('Highlights applied');
  };

  const clearHighlights = (selection?: { index: number; length: number } | null) => {
    if (!quillRef.current) return;

    const quill = quillRef.current.getEditor();
    if (!quill) return;

    // Get the text length
    const textLength = quill.getText().length;
    
    // Only remove background color formatting, not the text itself
    if (textLength > 0) {
      try {
        // Remove only background color formatting
        quill.formatText(0, textLength, {
          'background': false
        });
      } catch (error) {
        console.error('Error clearing highlights:', error);
      }
    }

    if (selection) {
      try {
        quill.setSelection(selection.index, selection.length, 'silent');
      } catch {
        // ignore
      }
    }
  };

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const newLanguage = event.target.value;
    setSelectedLanguage(newLanguage);
    // Save the selected language as default
    localStorage.setItem('defaultSpeechLanguage', newLanguage);
    if (recognitionRef.current) {
      recognitionRef.current.lang = newLanguage;
    }
  };

  const handleTextChange = (content: string) => {
    if (/<\s*video\b|<\s*audio\b/i.test(content) && quillRef.current?.getEditor?.()) {
      const quill = quillRef.current.getEditor();
      const root = quill.root;
      let moved = false;
      root.querySelectorAll('video, audio').forEach((node: Element) => {
        const el = node as HTMLMediaElement;
        const src =
          el.getAttribute('src')?.trim() ||
          el.querySelector('source[src]')?.getAttribute('src')?.trim() ||
          '';
        if (src) {
          moved = true;
          const isVid = el.tagName === 'VIDEO';
          queueMicrotask(() => {
            appendExternalPlaybackRef.current({
              key: `inline:${Date.now()}:${Math.random().toString(36).slice(2)}`,
              url: toAbsoluteMediaUrlForPlayback(src),
              mimeType: isVid ? 'video/mp4' : 'audio/mpeg',
            });
          });
        }
        node.remove();
      });
      if (moved) {
        setText(root.innerHTML);
        if (error && !error.includes('already exists')) {
          setError('');
        }
        setSaveAttempted(false);
        return;
      }
    }
    setText(content);
    if (error && !error.includes('already exists')) {
      setError('');
    }
    setSaveAttempted(false);
  };

  const insertMediaAtCursor = useCallback(
    async (file: File, opts?: { forceEmbedImage?: boolean }) => {
      if (readOnly || !quillRef.current) return;
      const quill = quillRef.current.getEditor?.();
      if (!quill) return;
      const kind = guessMediaKind(file);
      if (!kind) {
        setError('Could not detect media type. Try a common image, video, or audio format.');
        return;
      }
      const embedImage = kind === 'image' && (opts?.forceEmbedImage === true || embedImageInEditor);
      const form = new FormData();
      form.append('file', file);
      const uploadProgressConfig = {
        onUploadProgress: (evt: { loaded: number; total?: number }) => {
          const total = evt.total;
          if (total && total > 0) {
            setMediaUploadBar({
              variant: 'determinate',
              value: Math.min(100, Math.round((evt.loaded * 100) / total)),
            });
          } else {
            setMediaUploadBar({ variant: 'indeterminate' });
          }
        },
      };
      try {
        setMediaUploadBar({ variant: 'indeterminate' });
        let path: string;
        let stagingId: string | undefined;
        let attachmentId: string | undefined;
        const imageExternal = kind === 'image' && !embedImage ? '&externalAttachment=true' : '';
        if (contentId) {
          const { data } = await api.post(
            `/content/media?contentId=${encodeURIComponent(contentId)}${imageExternal}`,
            form,
            uploadProgressConfig,
          );
          path = data.url as string;
          attachmentId = data.attachmentId as string | undefined;
        } else {
          const stageQuery = kind === 'image' && !embedImage ? '?externalAttachment=true' : '';
          const { data } = await api.post(`/content/media/stage${stageQuery}`, form, uploadProgressConfig);
          path = data.url as string;
          stagingId = data.stagingId as string | undefined;
          if (stagingId) {
            stagedMediaIdsRef.current = [...stagedMediaIdsRef.current, stagingId];
          }
        }
        const origin = getApiOrigin();
        let absolute = path.startsWith('http') ? path : `${origin}${path}`;
        const token = localStorage.getItem('token');
        if (token) {
          absolute += `${absolute.includes('?') ? '&' : '?'}access_token=${encodeURIComponent(token)}`;
        }
        const live = quill.getSelection(true);
        const docLen = quill.getLength();
        const maxIndex = Math.max(0, docLen - 1);
        let idx = live
          ? live.index
          : lastQuillCaretRef.current
            ? lastQuillCaretRef.current.index
            : maxIndex;
        idx = Math.max(0, Math.min(idx, maxIndex));
        if (embedImage) {
          quill.focus();
          quill.setSelection(idx, 0, 'silent');
          quill.insertEmbed(idx, 'image', absolute, 'user');
          quill.setSelection(idx + 1, 0, 'silent');
          setText(quill.root.innerHTML);
          setMediaInsertNotice('Image added.');
          window.setTimeout(() => {
            const root = quill.root;
            const nodes = root.querySelectorAll('img');
            const last = nodes[nodes.length - 1];
            last?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }, 150);
        } else {
          const mime =
            (file.type || '').trim() ||
            (kind === 'image' ? 'image/png' : kind === 'video' ? 'video/mp4' : 'audio/mpeg');
          const rowKey = stagingId ? `st:${stagingId}` : attachmentId ? `opt:${attachmentId}` : `row:${Date.now()}`;
          setExtraPlaybackRows((prev) => {
            if (prev.some((p) => p.url === absolute)) return prev;
            return [...prev, { key: rowKey, url: absolute, mimeType: mime, fileName: file.name }];
          });
          setMediaInsertNotice(
            kind === 'image'
              ? 'Image attached — open Media in the toolbar and tap View.'
              : kind === 'video'
                ? 'Video attached — open Media in the toolbar and tap Play.'
                : 'Audio attached — open Media in the toolbar and tap Play.',
          );
          window.setTimeout(() => {
            mainMediaMenuButtonRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            expandedMediaMenuButtonRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }, 150);
        }
      } catch (e: unknown) {
        console.error('Media upload failed', e);
        const ax = e as { response?: { data?: unknown } };
        const data = ax?.response?.data;
        const msg =
          typeof data === 'string'
            ? data
            : data && typeof data === 'object' && 'message' in data && typeof (data as { message: unknown }).message === 'string'
              ? (data as { message: string }).message
              : null;
        setError(msg || 'Failed to upload media');
      } finally {
        setMediaUploadBar(null);
      }
    },
    [contentId, readOnly, embedImageInEditor],
  );

  insertMediaAtCursorRef.current = (file: File, opts?: { forceEmbedImage?: boolean }) => {
    void insertMediaAtCursor(file, opts);
  };

  const handleDeletePlaybackRow = useCallback(
    async (row: ExternalPlaybackRow) => {
      if (readOnly || !isRowPersistedOrStagedMedia(row)) return;
      setMediaDeletingKey(row.key);
      try {
        if (row.key.startsWith('st:')) {
          const stagingId = row.key.slice(3);
          await api.delete(`/content/media-staging/${encodeURIComponent(stagingId)}`);
          stagedMediaIdsRef.current = stagedMediaIdsRef.current.filter((id) => id !== stagingId);
          setExtraPlaybackRows((prev) => prev.filter((r) => r.key !== row.key));
          return;
        }
        if (!contentId) {
          setError('Save the content first before removing uploaded media.');
          return;
        }
        const attachmentId = row.key.slice(4);
        await api.delete(
          `/content/media/${encodeURIComponent(contentId)}/${encodeURIComponent(attachmentId)}`,
        );
        setExtraPlaybackRows((prev) => prev.filter((r) => r.key !== row.key));
        onMediaAttachmentsChanged?.();
      } catch (e: unknown) {
        console.error('Media delete failed', e);
        const ax = e as { response?: { data?: unknown } };
        const data = ax?.response?.data;
        const msg =
          typeof data === 'string'
            ? data
            : data && typeof data === 'object' && 'message' in data && typeof (data as { message: unknown }).message === 'string'
              ? (data as { message: string }).message
              : null;
        setError(msg || 'Failed to remove media');
      } finally {
        setMediaDeletingKey(null);
      }
    },
    [readOnly, contentId, onMediaAttachmentsChanged],
  );

  // Focus/blur + paste: custom paste uses dangerouslyPasteHTML — must strip <video>/<audio> or they embed in the editor.
  // Drop: route video/audio files to the same upload path as the toolbar (external players).
  useLayoutEffect(() => {
    let cancelled = false;
    let disposer: (() => void) | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const attach = () => {
      const editor = quillRef.current?.getEditor?.();
      if (!editor || cancelled) return false;
      disposer?.();
      disposer = undefined;
      const editorElement = editor.root;
      const quill = editor;

      const handleFocus = () => {
        setIsEditing(true);
      };
      const handleBlur = () => {
        setIsEditing(false);
      };

      const handlePaste = (e: ClipboardEvent) => {
        if (!e.clipboardData) return;

        const active = document.activeElement as HTMLElement | null;
        const target = e.target as HTMLElement | null;
        if (!(active && editorElement.contains(active)) && !(target && editorElement.contains(target))) {
          return;
        }

        e.preventDefault();

        const htmlRaw = e.clipboardData.getData('text/html');
        const plain = e.clipboardData.getData('text/plain');
        const html = htmlRaw?.trim() ? htmlRaw : '';
        if (!html && !plain) return;

        quill.focus();
        const sel = quill.getSelection(true);
        const index = sel ? sel.index : quill.getLength();

        try {
          if (html) {
            const { safeHtml, media } = extractMediaFromPasteHtml(html);
            media.forEach((m, i) => {
              queueMicrotask(() => {
                appendExternalPlaybackRef.current({
                  key: `paste:html:${Date.now()}:${i}`,
                  url: m.url,
                  mimeType: m.mimeType,
                });
              });
            });
            const toPaste = safeHtml.trim() ? safeHtml : '<p><br></p>';
            quill.clipboard.dangerouslyPasteHTML(index, toPaste, 'user');
          } else {
            quill.insertText(index, plain, 'user');
          }
          const newSel = quill.getSelection(true);
          if (newSel) {
            quill.setSelection(newSel.index, 0, 'silent');
          }
          // Quill can still leave stray media nodes after HTML paste; move them to external players.
          const root = quill.root;
          root.querySelectorAll('video, audio').forEach((node: Element) => {
            const el = node as HTMLMediaElement;
            const src =
              el.getAttribute('src')?.trim() ||
              el.querySelector('source[src]')?.getAttribute('src')?.trim() ||
              '';
            if (src) {
              const isVid = el.tagName === 'VIDEO';
              queueMicrotask(() => {
                appendExternalPlaybackRef.current({
                  key: `paste:stray:${Date.now()}`,
                  url: toAbsoluteMediaUrlForPlayback(src),
                  mimeType: isVid ? 'video/mp4' : 'audio/mpeg',
                });
              });
            }
            node.remove();
          });
          setText(quill.root.innerHTML);
        } catch (err) {
          console.error('Paste failed:', err);
          try {
            quill.insertText(index, plain || '', 'user');
            quill.setSelection(index + (plain || '').length, 0, 'silent');
            setText(quill.root.innerHTML);
          } catch {
            // ignore
          }
        }
      };

      const handleDrop = (e: DragEvent) => {
        if (readOnly) return;
        if (!e.dataTransfer?.files?.length) return;
        const target = e.target as HTMLElement | null;
        if (!(target && editorElement.contains(target))) return;
        const files = Array.from(e.dataTransfer.files);
        const videoAudio = files.filter((f) => {
          const k = guessMediaKind(f);
          return k === 'video' || k === 'audio';
        });
        if (videoAudio.length === 0) return;
        e.preventDefault();
        e.stopPropagation();
        quill.focus();
        for (const f of videoAudio) {
          insertMediaAtCursorRef.current(f);
        }
      };

      editorElement.addEventListener('focus', handleFocus);
      editorElement.addEventListener('blur', handleBlur);
      editorElement.addEventListener('paste', handlePaste);
      editorElement.addEventListener('drop', handleDrop, true);

      disposer = () => {
        editorElement.removeEventListener('focus', handleFocus);
        editorElement.removeEventListener('blur', handleBlur);
        editorElement.removeEventListener('paste', handlePaste);
        editorElement.removeEventListener('drop', handleDrop, true);
      };
      return true;
    };

    if (!attach()) {
      timeoutId = setTimeout(() => {
        if (!cancelled) attach();
      }, 0);
    }

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      disposer?.();
    };
  }, [insertMediaAtCursor, readOnly, expandedEditorOpen]);

  const quillModules = useMemo(() => {
    const Delta = Quill.import('delta') as any;
    const hasMediaEmbed = (delta: any, key: string) =>
      (delta?.ops || []).some((op: any) => op.insert && typeof op.insert === 'object' && op.insert[key]);
    return {
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ color: [] }, { background: [] }],
          [{ align: ['', 'center', 'right', 'justify'] }],
          ['link', 'image', 'clean'],
        ],
        handlers: {
          image: function (this: { quill?: { getSelection: (f: boolean) => { index: number; length: number } | null } }) {
            const q = this.quill;
            if (q) {
              const r = q.getSelection(true);
              if (r) lastQuillCaretRef.current = { index: r.index, length: r.length };
            }
            imageInsertForceEmbedRef.current = true;
            imageInputRef.current?.click();
          },
        },
      },
      clipboard: {
        matchers: [
          [
            'IMG',
            (node: HTMLElement, delta: any) => {
              if (node.tagName !== 'IMG') return delta;
              const src = node.getAttribute('src');
              if (!src) return delta;
              const wAttr = node.getAttribute('width');
              const wPx = /^(\d+(?:\.\d+)?)px$/i.exec(node.style.width || '');
              const w = wAttr || (wPx && wPx[1]);
              const hAttr = node.getAttribute('height');
              const hPx = /^(\d+(?:\.\d+)?)px$/i.exec(node.style.height || '');
              const h = hAttr || (hPx && hPx[1]);
              const insert =
                w || h
                  ? {
                      image: {
                        src,
                        ...(w ? { width: String(w) } : {}),
                        ...(h ? { height: String(h) } : {}),
                      },
                    }
                  : { image: src };
              return new Delta().insert(insert);
            },
          ],
          [
            'VIDEO',
            (node: HTMLElement, delta: any) => {
              const src = node.getAttribute('src');
              if (!src || node.tagName !== 'VIDEO') return delta;
              if (hasMediaEmbed(delta, 'simpleVideo')) return delta;
              const url = toAbsoluteMediaUrlForPlayback(src);
              queueMicrotask(() => {
                appendExternalPlaybackRef.current({
                  key: `paste:video:${Date.now()}`,
                  url,
                  mimeType: 'video/mp4',
                });
              });
              return new Delta().insert('\n');
            },
          ],
          [
            'AUDIO',
            (node: HTMLElement, delta: any) => {
              const src = node.getAttribute('src');
              if (!src || node.tagName !== 'AUDIO') return delta;
              if (hasMediaEmbed(delta, 'simpleAudio')) return delta;
              const url = toAbsoluteMediaUrlForPlayback(src);
              queueMicrotask(() => {
                appendExternalPlaybackRef.current({
                  key: `paste:audio:${Date.now()}`,
                  url,
                  mimeType: 'audio/mpeg',
                });
              });
              return new Delta().insert('\n');
            },
          ],
        ],
      },
    };
  }, []);

  const quillFormats = useMemo(
    () =>
      [
        'header',
        'bold',
        'italic',
        'underline',
        'strike',
        'list',
        'bullet',
        'color',
        'background',
        'align',
        'link',
        'image',
      ] as const,
    [],
  );

  const editorAreaSx = useCallback(
    (expanded: boolean) => (theme: Theme) => {
      const isDark = theme.palette.mode === 'dark';
      const toolbarIcon = isDark ? theme.palette.grey[200] : theme.palette.grey[800];
      const toolbarIconActive = isDark ? theme.palette.primary.light : theme.palette.primary.main;
      const editingToolbarChrome = {
        backgroundColor: isDark
          ? alpha(theme.palette.common.white, 0.1)
          : alpha(theme.palette.common.black, 0.04),
        border: `1px solid ${theme.palette.divider}`,
        borderTopLeftRadius: theme.shape.borderRadius,
        borderTopRightRadius: theme.shape.borderRadius,
        boxShadow: isDark ? `inset 0 -1px 0 ${alpha(theme.palette.common.black, 0.35)}` : undefined,
        '& .ql-picker': {
          color: theme.palette.text.primary,
        },
        '& .ql-picker-options': {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.divider}`,
        },
        '& .ql-picker.ql-expanded .ql-picker-label': {
          color: theme.palette.text.secondary,
          borderColor: theme.palette.divider,
        },
        '& .ql-picker.ql-expanded .ql-picker-label .ql-fill': {
          fill: theme.palette.text.secondary,
        },
        '& .ql-picker.ql-expanded .ql-picker-label .ql-stroke': {
          stroke: theme.palette.text.secondary,
        },
        '& .ql-picker.ql-expanded .ql-picker-options': {
          borderColor: theme.palette.divider,
        },
        '& .ql-stroke': {
          stroke: toolbarIcon,
        },
        '& .ql-stroke-miter': {
          stroke: toolbarIcon,
        },
        '& .ql-fill': {
          fill: toolbarIcon,
        },
        '& .ql-stroke.ql-fill': {
          fill: toolbarIcon,
        },
        '& button:hover .ql-stroke, & button:focus .ql-stroke, & button.ql-active .ql-stroke, & .ql-picker-label:hover .ql-stroke, & .ql-picker-label.ql-active .ql-stroke, & .ql-picker-item:hover .ql-stroke, & .ql-picker-item.ql-selected .ql-stroke':
          {
            stroke: `${toolbarIconActive} !important`,
          },
        '& button:hover .ql-stroke-miter, & button:focus .ql-stroke-miter, & button.ql-active .ql-stroke-miter, & .ql-picker-label:hover .ql-stroke-miter, & .ql-picker-label.ql-active .ql-stroke-miter, & .ql-picker-item:hover .ql-stroke-miter, & .ql-picker-item.ql-selected .ql-stroke-miter':
          {
            stroke: `${toolbarIconActive} !important`,
          },
        '& button:hover .ql-fill, & button:focus .ql-fill, & button.ql-active .ql-fill, & .ql-picker-label:hover .ql-fill, & .ql-picker-label.ql-active .ql-fill, & .ql-picker-item:hover .ql-fill, & .ql-picker-item.ql-selected .ql-fill, & button:hover .ql-stroke.ql-fill, & button:focus .ql-stroke.ql-fill, & button.ql-active .ql-stroke.ql-fill':
          {
            fill: `${toolbarIconActive} !important`,
          },
        '& .ql-color-picker .ql-picker-item.ql-selected, & .ql-color-picker .ql-picker-item:hover': {
          borderColor: theme.palette.text.primary,
        },
      };
      return {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      '& .ql-editor': {
        fontSize: 'var(--app-font-size, 14px)',
        flex: 1,
        minHeight: expanded ? 'min(72vh, calc(100dvh - 140px))' : '200px',
        // RTL script (or RTL speech dictation): align right — Quill defaults to left.
        ...(editorRtlLayout
          ? { direction: 'rtl' as const, textAlign: 'right !important' as const }
          : { direction: 'ltr' as const }),
        '& .ql-align-right': {
          textAlign: 'right',
        },
        '& .ql-align-center': {
          textAlign: 'center',
        },
        ...(editorRtlLayout
          ? {
              '& .ql-align-left': {
                textAlign: 'right !important',
              },
            }
          : {
              '& .ql-align-left': {
                textAlign: 'left',
              },
            }),
        '& p': {
          margin: 0,
          marginBottom: '0.35em',
        },
        '& p:last-child': {
          marginBottom: 0,
        },
      },
      ...(editorRtlLayout
        ? {
            '& .ql-editor p:not(.ql-align-center), & .ql-editor li:not(.ql-align-center)': {
              textAlign: 'right !important',
            },
          }
        : {}),
      '& .ql-editor.ql-blank::before': {
        color: theme.palette.text.disabled,
      },
      '& .ql-toolbar': {
        fontSize: 'var(--app-font-size, 14px)',
        ...(readOnly
          ? {
              pointerEvents: 'none',
              userSelect: 'none',
              opacity: 0.5,
              cursor: 'default',
              filter: 'grayscale(35%)',
              backgroundColor: theme.palette.action.disabledBackground,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }
          : editingToolbarChrome),
      },
      '& .ql-container.ql-snow': {
        borderColor: theme.palette.divider,
      },
      '& .ql-container .ql-tooltip': {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
        boxShadow: theme.shadows[4],
        '& a': {
          color: theme.palette.primary.main,
        },
        '& input[type="text"]': {
          borderColor: theme.palette.divider,
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.06) : theme.palette.common.white,
        },
      },
      '& .ql-container': {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      },
      '& .ql-editor img': {
        maxWidth: '100%',
      },
      '& .ql-editor img.narralizer-img-selected': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
      },
      /* Video/audio must not appear in the document — use Play buttons + in-tab modal */
      '& .ql-editor video, & .ql-editor audio': {
        display: 'none !important',
      },
    };
    },
    [readOnly, editorRtlLayout],
  );

  const handleTextFocus = () => {
    setIsEditing(true);
  };

  const handleTextBlur = () => {
    setIsEditing(false);
  };

  const handleSubcaseChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSubcase(event.target.value);
    if (saveAttempted && error && !error.includes('already exists')) {
        setError('');
      }
  };

  const handleSave = () => {
    setSaveAttempted(true);
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }
    if (!subcase.trim()) {
      setError('Subcase is required');
      return;
    }
    const textWithoutHtml = text.replace(/<[^>]*>/g, '').trim();
    if (!contentEditorHasSavableBody(text, allPlaybackRows.length, stagedMediaIdsRef.current.length)) {
      setError('Add text or attach media before saving');
      return;
    }

    const quill = quillRef.current?.getEditor?.();
    let richTextHtml = stripVideoAudioFromHtml(stripMediaAccessTokens(quill?.root?.innerHTML ?? text));
    let richTextDelta = quill
      ? stripMediaAccessTokens(JSON.stringify(stripVideoAudioFromDelta(quill.getContents() as { ops?: unknown[] })))
      : stripMediaAccessTokens(JSON.stringify({ ops: [{ insert: textWithoutHtml + '\n' }] }));

    // Plain text used for snippets/search/embeddings and duplicate detection
    const cleanText = (
      quill
        ? htmlToPlainWithBlockSpaces(quill.root.innerHTML)
        : textWithoutHtml
    )
      .replace(/\s+/g, ' ')
      .trim();

    // Check for duplicate content
    if (category && hasDuplicateContent(category, subcase, cleanText, contentId, richTextHtml)) {
      setError(getDuplicateContentMessage(subcase));
      setSaveAttempted(true);
      return;
    }

    const stagedForSave = contentId ? [] : [...stagedMediaIdsRef.current];
    setExpandedEditorOpen(false);
    onSave(subcase, cleanText, richTextHtml, richTextDelta, stagedForSave);
  };

  const handleCancel = () => {
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }
    const toDiscard = [...stagedMediaIdsRef.current];
    stagedMediaIdsRef.current = [];
    setExpandedEditorOpen(false);
    setSaveAttempted(false);
    setError('');
    setExtraPlaybackRows([]);
    discardStagedMediaOnServer(toDiscard);
    onCancel();
  };

  const toggleRecording = async () => {
    const recognition = ensureRecognition();
    if (!recognition) {
      console.error('Speech recognition not supported');
      return;
    }

    if (isRecording) {
      recordingStopRequestedRef.current = true;
      try {
        recognition.stop();
      } finally {
        stopMicStream();
        pendingStartRef.current = false;
        setIsRecording(false);
      }
    } else {
      try {
        recordingDidStartRef.current = false;
        pendingStartRef.current = true;
        setIsStartingRecording(true);
        const ok = await ensureMicPermission();
        if (!ok) {
          pendingStartRef.current = false;
          setIsStartingRecording(false);
          setIsRecording(false);
          return;
        }
        recognition.start();
      } catch (e) {
        // start() can throw on first use in some browsers; do not flip UI state unless it started.
        console.error('Failed to start speech recognition:', e);
        pendingStartRef.current = false;
        setIsStartingRecording(false);
        setIsRecording(false);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (speechFinalizeTimerRef.current != null) {
        window.clearTimeout(speechFinalizeTimerRef.current);
      }
      stopMicStream();
    };
  }, []);

  return (
    <EditorContainer>
      <style>{highlightStyles}{editorRtlStyles}</style>
      {mediaUploadBar && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Uploading media…
          </Typography>
          <LinearProgress
            variant={mediaUploadBar.variant}
            value={mediaUploadBar.variant === 'determinate' ? mediaUploadBar.value : undefined}
          />
        </Box>
      )}
      {mediaInsertNotice && (
        <Alert severity="success" onClose={() => setMediaInsertNotice(null)} sx={{ mb: 1 }}>
          {mediaInsertNotice}
        </Alert>
      )}
      {showWarning && (
        <WarningBanner>
          <WarningIcon />
          <Typography variant="body2">
            You have unsaved changes. Please save or cancel before navigating away.
          </Typography>
        </WarningBanner>
      )}
       {saveAttempted && error && text.replace(/<[^>]*>/g, '').trim() && subcase.trim() && (
            <WarningBanner sx={{ backgroundColor: 'error.light', color: 'error.contrastText', border: '1px solid error.main' }}>
              <WarningIcon />
              <Typography variant="body2">
                {error}
              </Typography>
            </WarningBanner>
          )}
      <TopBar>
        <DropdownFieldTooltip title={getToolbarTooltipTextForState('content-subcase', true)}>
          <Autocomplete
            freeSolo={!subcaseSelectOnly}
            options={subcases}
            value={subcase}
            disabled={readOnly}
            onChange={(_, newValue) => {
              const v = newValue ?? '';
              setSubcase(v);
              onSubcaseChange?.(v);
               if (error && !error.includes('already exists')) {
                 setError('');
               }
            }}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === 'input' || reason === 'clear') {
                setSubcase(newInputValue);
                onSubcaseChange?.(newInputValue);
              }
              if (error && !error.includes('already exists')) {
                 setError('');
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sub Case"
                variant="outlined"
                size="small"
                disabled={readOnly}
                error={!!error && !subcase.trim() && saveAttempted}
                helperText={!!error && !subcase.trim() && saveAttempted ? error : ''}
              />
            )}
            size="small"
            sx={{ width: 180, flexShrink: 0 }}
          />
        </DropdownFieldTooltip>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <DropdownFieldTooltip title={getToolbarTooltipTextForState('content-language', true)}>
            <LanguageSelect size="small">
              <InputLabel>Language</InputLabel>
              <Select
                value={selectedLanguage}
                onChange={handleLanguageChange}
                label="Language"
                disabled={readOnly}
              >
                {languages.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </MenuItem>
                ))}
              </Select>
            </LanguageSelect>
          </DropdownFieldTooltip>
          <Tooltip title={getToolbarTooltipTextForState(isRecording ? 'speech-recording-stop' : 'speech-recording', true)} placement="top">
            <span>
              <StyledIconButton
                onClick={toggleRecording}
                disabled={readOnly}
                sx={{
                  ml: 0,
                  backgroundColor: isRecording ? 'error.main' : 'transparent',
                  color: isRecording ? 'error.contrastText' : 'inherit',
                  '&:hover': {
                    backgroundColor: isRecording ? 'error.dark' : 'action.hover',
                  },
                  animation: isRecording ? 'pulse 1.5s ease-in-out infinite' : 'none',
                  '@keyframes pulse': {
                    '0%': {
                      boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.7)',
                    },
                    '70%': {
                      boxShadow: '0 0 0 10px rgba(211, 47, 47, 0)',
                    },
                    '100%': {
                      boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)',
                    },
                  },
                }}
              >
                <MicIcon />
              </StyledIconButton>
            </span>
          </Tooltip>
          {isStartingRecording && <CircularProgress size={16} sx={{ alignSelf: 'center' }} />}
          {isRecording && !isStartingRecording && (
            <Typography variant="caption" sx={{ alignSelf: 'center', color: 'success.main', whiteSpace: 'nowrap' }}>
              Listening…
            </Typography>
          )}
          <input
            type="file"
            accept="image/*"
            ref={imageInputRef}
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              const forceEmbed = imageInsertForceEmbedRef.current;
              imageInsertForceEmbedRef.current = false;
              if (f) void insertMediaAtCursor(f, { forceEmbedImage: forceEmbed });
            }}
          />
          <input
            type="file"
            accept="video/*"
            ref={videoInputRef}
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void insertMediaAtCursor(f);
            }}
          />
          <input
            type="file"
            accept="audio/*"
            ref={audioInputRef}
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void insertMediaAtCursor(f);
            }}
          />
          <Tooltip title={getToolbarTooltipTextForState('content-insert-image', !readOnly)} placement="top">
            <span
              onMouseDown={(e) => {
                e.preventDefault();
                const quill = quillRef.current?.getEditor?.();
                if (quill) {
                  const r = quill.getSelection(true);
                  if (r) lastQuillCaretRef.current = { index: r.index, length: r.length };
                }
              }}
            >
              <StyledIconButton
                onClick={() => {
                  imageInsertForceEmbedRef.current = false;
                  imageInputRef.current?.click();
                }}
                disabled={readOnly}
                size="small"
                sx={{ ml: 0 }}
              >
                <ImageIcon />
              </StyledIconButton>
            </span>
          </Tooltip>
          <Tooltip title={getToolbarTooltipTextForState('content-embed-image', !readOnly)} placement="top">
            <FormControlLabel
              sx={{ mr: 0, ml: 0, whiteSpace: 'nowrap' }}
              control={
                <Switch
                  size="small"
                  checked={embedImageInEditor}
                  onChange={(_, v) => setEmbedImageInEditor(v)}
                  disabled={readOnly}
                  inputProps={{ 'aria-label': 'Embed new images in document body' }}
                />
              }
              label={<Typography variant="caption">Embed image</Typography>}
            />
          </Tooltip>
          <Tooltip title="Insert video" placement="top">
            <span>
              <StyledIconButton onClick={() => videoInputRef.current?.click()} disabled={readOnly} size="small" sx={{ ml: 0 }}>
                <VideocamIcon />
              </StyledIconButton>
            </span>
          </Tooltip>
          <Tooltip title="Insert audio" placement="top">
            <span>
              <StyledIconButton onClick={() => audioInputRef.current?.click()} disabled={readOnly} size="small" sx={{ ml: 0 }}>
                <AudioFileIcon />
              </StyledIconButton>
            </span>
          </Tooltip>
          {allPlaybackRows.length > 0 && (
            <>
              <Tooltip title={getToolbarTooltipTextForState('content-attached-media', true)}>
                <Badge
                  badgeContent={allPlaybackRows.length}
                  color="primary"
                  overlap="rectangular"
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <Button
                    ref={mainMediaMenuButtonRef}
                    variant="outlined"
                    size="small"
                    className="content-media-play-button"
                    startIcon={<PermMediaOutlinedIcon />}
                    onClick={(e) => setAttachedMediaAnchorEl(e.currentTarget)}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Media
                  </Button>
                </Badge>
              </Tooltip>
              <AttachedMediaPopover
                anchorEl={attachedMediaAnchorEl}
                onClose={() => setAttachedMediaAnchorEl(null)}
                rows={allPlaybackRows}
                keyPrefix="main"
                readOnly={readOnly}
                mediaDeletingKey={mediaDeletingKey}
                onMediaPlay={setMediaPlayerRow}
                onDeleteRow={handleDeletePlaybackRow}
              />
            </>
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto', flexShrink: 0 }}>
          <Tooltip title={getToolbarTooltipTextForState('content-save', true)} placement="top">
            <span>
              <Button variant="contained" onClick={handleSave} disabled={readOnly}>
                Save
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={getToolbarTooltipTextForState('content-cancel', true)} placement="top">
            <span>
              <Button variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </TopBar>
      <Tooltip title={getToolbarTooltipTextForState('content-text-area', true)} placement="top">
        <EditorWrapper className={editorRtlLayout ? 'narralizer-editor-rtl' : undefined}>
          {!expandedEditorOpen ? (
            <Box
              sx={{
                position: 'relative',
                height: '100%',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Box sx={editorAreaSx(false)}>
                <ReactQuill
                  value={text}
                  onChange={handleTextChange}
                  onChangeSelection={(range) => {
                    if (range) lastQuillCaretRef.current = { index: range.index, length: range.length };
                  }}
                  readOnly={readOnly}
                  modules={quillModules}
                  formats={quillFormats as unknown as string[]}
                  style={{
                    border:
                      saveAttempted &&
                      !contentEditorHasSavableBody(
                        text,
                        allPlaybackRows.length,
                        stagedMediaIdsRef.current.length,
                      )
                        ? '1px solid #d32f2f'
                        : '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                  }}
                  ref={quillRef}
                />
              </Box>
              {!readOnly && (
                <Box
                  sx={(theme) => ({
                    position: 'absolute',
                    top: 0,
                    right: theme.spacing(1),
                    zIndex: 2,
                    // Quill Snow toolbar row height; keeps the control vertically centered on the toolbar
                    height: 42,
                    display: 'flex',
                    alignItems: 'center',
                    pointerEvents: 'none',
                  })}
                >
                  <Tooltip title={getToolbarTooltipTextForState('content-expand-editor', true)} placement="bottom">
                    <span style={{ pointerEvents: 'auto' }}>
                      <IconButton
                        size="small"
                        onClick={() => setExpandedEditorOpen(true)}
                        aria-label="Expand editor to full screen"
                        sx={(theme) => ({
                          bgcolor: 'background.paper',
                          border: `1px solid ${theme.palette.divider}`,
                          boxShadow: 2,
                          color: 'primary.dark',
                          '&:hover': { bgcolor: 'action.hover', color: 'primary.dark' },
                        })}
                      >
                        <FullscreenIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                The editor is open in the full-screen window. Close that window to continue editing here.
              </Typography>
            </Box>
          )}
          {saveAttempted &&
            !contentEditorHasSavableBody(
              text,
              allPlaybackRows.length,
              stagedMediaIdsRef.current.length,
            ) && <ErrorText variant="caption">Add text or attach media before saving</ErrorText>}
        </EditorWrapper>
      </Tooltip>

      <Dialog
        fullScreen
        open={expandedEditorOpen}
        onClose={() => setExpandedEditorOpen(false)}
        PaperProps={{ sx: { display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' } }}
      >
        <AppBar position="sticky" color="default" elevation={1}>
          <Toolbar sx={{ gap: 1, pr: 1 }}>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
              Expanded editor
            </Typography>
            {allPlaybackRows.length > 0 && (
              <>
                <Tooltip title={getToolbarTooltipTextForState('content-attached-media', true)}>
                  <Badge
                    badgeContent={allPlaybackRows.length}
                    color="primary"
                    overlap="rectangular"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <Button
                      ref={expandedMediaMenuButtonRef}
                      variant="outlined"
                      size="small"
                      className="content-media-play-button"
                      color="inherit"
                      startIcon={<PermMediaOutlinedIcon />}
                      onClick={(e) => setExpandedMediaAnchorEl(e.currentTarget)}
                      sx={{ whiteSpace: 'nowrap', borderColor: 'divider' }}
                    >
                      Media
                    </Button>
                  </Badge>
                </Tooltip>
                <AttachedMediaPopover
                  anchorEl={expandedMediaAnchorEl}
                  onClose={() => setExpandedMediaAnchorEl(null)}
                  rows={allPlaybackRows}
                  keyPrefix="expanded"
                  readOnly={readOnly}
                  mediaDeletingKey={mediaDeletingKey}
                  onMediaPlay={setMediaPlayerRow}
                  onDeleteRow={handleDeletePlaybackRow}
                />
              </>
            )}
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setExpandedEditorOpen(false)}
              aria-label="Close expanded editor"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, p: 2, overflow: 'auto' }}>
          <Box
            className={editorRtlLayout ? 'narralizer-editor-rtl' : undefined}
            sx={(theme) => ({
              ...editorAreaSx(true)(theme),
              '& .quill': {
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              },
            })}
          >
            <ReactQuill
              value={text}
              onChange={handleTextChange}
              onChangeSelection={(range) => {
                if (range) lastQuillCaretRef.current = { index: range.index, length: range.length };
              }}
              readOnly={readOnly}
              modules={quillModules}
              formats={quillFormats as unknown as string[]}
              style={{
                border:
                  saveAttempted &&
                  !contentEditorHasSavableBody(
                    text,
                    allPlaybackRows.length,
                    stagedMediaIdsRef.current.length,
                  )
                    ? '1px solid #d32f2f'
                    : '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
              ref={quillRef}
            />
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={!!mediaPlayerRow}
        onClose={() => setMediaPlayerRow(null)}
        fullScreen={mediaViewerFullScreen}
        maxWidth={false}
        fullWidth={mediaViewerFullScreen}
        scroll="paper"
        PaperProps={{
          ref: mediaViewerPaperRef,
          sx: mediaViewerFullScreen
            ? {
                m: 0,
                height: '100%',
                maxHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                overflow: 'hidden',
              }
            : {
                display: 'flex',
                flexDirection: 'column',
                m: 1,
                width: 'min(1280px, calc(100vw - 24px))',
                minWidth: 420,
                maxWidth: 'calc(100vw - 16px)',
                maxHeight: 'min(92vh, 960px)',
                minHeight: 320,
                resize: 'both',
                overflow: 'hidden',
                bgcolor: 'background.paper',
              },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            py: 1,
            px: 1.5,
            flexShrink: 0,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography component="span" variant="subtitle1" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {mediaPlayerRow?.fileName ||
              ((() => {
                const m = (mediaPlayerRow?.mimeType ?? '').toLowerCase();
                if (m.startsWith('video/')) return 'Video';
                if (m.startsWith('image/')) return 'Image';
                return 'Audio';
              })())}
          </Typography>
          <Tooltip title={mediaViewerFullScreen ? 'Exit full screen' : 'Full screen'}>
            <IconButton
              aria-label={mediaViewerFullScreen ? 'Exit full screen' : 'Full screen'}
              onClick={() => setMediaViewerFullScreen((v) => !v)}
              size="small"
              sx={{ color: 'text.primary' }}
            >
              {mediaViewerFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
          <IconButton
            edge="end"
            aria-label="Close player"
            onClick={() => setMediaPlayerRow(null)}
            size="small"
            sx={{ color: 'text.primary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={(theme) => {
            const isImage = (mediaPlayerRow?.mimeType ?? '').toLowerCase().startsWith('image/');
            return {
              flex: 1,
              minHeight: 0,
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              bgcolor: isImage
                ? theme.palette.mode === 'dark'
                  ? theme.palette.grey[900]
                  : theme.palette.grey[100]
                : '#0a0a0a',
            };
          }}
        >
          {mediaPlayerRow &&
            ((() => {
              const m = (mediaPlayerRow.mimeType ?? '').toLowerCase();
              if (m.startsWith('video/')) {
                return (
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#000',
                    }}
                  >
                    <video
                      key={mediaPlayerRow.url}
                      controls
                      playsInline
                      preload="metadata"
                      src={mediaPlayerRow.url}
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        display: 'block',
                        background: '#000',
                      }}
                    />
                  </Box>
                );
              }
              if (m.startsWith('image/')) {
                return (
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'auto',
                      p: { xs: 1, sm: 1.5 },
                    }}
                  >
                    <img
                      key={mediaPlayerRow.url}
                      src={mediaPlayerRow.url}
                      alt={mediaPlayerRow.fileName || 'Attached image'}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                );
              }
              return (
                <Box sx={{ flex: 1, minHeight: 0, p: 2, bgcolor: 'grey.900', display: 'flex', alignItems: 'center' }}>
                  <audio
                    key={mediaPlayerRow.url}
                    controls
                    preload="metadata"
                    src={mediaPlayerRow.url}
                    style={{ width: '100%' }}
                  />
                </Box>
              );
            })())}
        </DialogContent>
      </Dialog>
    </EditorContainer>
  );
});

export default ContentEditor;